import { useState, useEffect, useCallback } from "react";

const PROJECT_KEY = "fixdesk:currentProject";

/** Read the shared project selection ('' when nothing has been chosen yet). */
export const getStoredProject = () => {
    try { return localStorage.getItem(PROJECT_KEY) || ''; } catch { return ''; }
};

const writeStoredProject = (id) => {
    try {
        if (id === '' || id === null || id === undefined) localStorage.removeItem(PROJECT_KEY);
        else localStorage.setItem(PROJECT_KEY, String(id));
    } catch { /* ignore */ }
};

/**
 * Keep `current` if it still exists in `projects`, otherwise adopt the first.
 * Call this once a project list loads, so a stored id that was deleted (or
 * belongs to another company) can't leave a page stuck on an empty selection.
 */
export const resolveProject = (projects, current) => {
    const list = projects || [];
    if (!list.length) return current;
    return list.some(p => String(p.id) === String(current)) ? current : String(list[0].id);
};

/**
 * Project selection shared by every page. Backed by localStorage so the choice
 * survives navigation and reloads, and mirrors into other open tabs.
 *
 * Drop-in replacement for useState('') — the value is always a string, which is
 * what FilterDropdown emits and compares against.
 */
export default function useCurrentProject() {
    const [currentProject, setState] = useState(getStoredProject);

    const setCurrentProject = useCallback((value) => {
        setState(prev => {
            const raw = typeof value === 'function' ? value(prev) : value;
            return (raw === null || raw === undefined) ? '' : String(raw);
        });
    }, []);

    useEffect(() => { writeStoredProject(currentProject); }, [currentProject]);

    // Mirror the choice when another tab changes it.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === PROJECT_KEY) setState(e.newValue || '');
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    return [currentProject, setCurrentProject];
}
